import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, TrendingUp, Clock, Users, MessageCircle, 
  PlayCircle, Settings, Plus, ChevronRight,
  Activity, BarChart3, PieChart, Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';
import useFlowStore from '../store/flowStore';
import dbService from '../services/dbService';

interface StatCard {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
}

const Dashboard: React.FC = () => {
  const { flows } = useFlowStore();
  const [stats, setStats] = useState({
    totalFlows: 0,
    activeFlows: 0,
    messagesProcessed: 0,
    successRate: 0,
  });

  useEffect(() => {
    // Calcola statistiche dai dati reali
    const analytics = dbService.getAnalytics();
    const todayStats = analytics.daily[0] || { messagesProcessed: 0 };
    const totalMessages = analytics.daily.reduce((sum, day) => sum + day.messagesProcessed, 0);
    
    // Calcola il tasso di successo medio
    let totalSuccessRate = 0;
    let flowCount = 0;
    Object.values(analytics.flowStats).forEach((stats: any) => {
      if (stats.totalExecutions > 0) {
        totalSuccessRate += (stats.successCount / stats.totalExecutions) * 100;
        flowCount++;
      }
    });
    const avgSuccessRate = flowCount > 0 ? totalSuccessRate / flowCount : 100;
    
    setStats({
      totalFlows: flows.length,
      activeFlows: flows.filter(f => f.isActive).length,
      messagesProcessed: totalMessages,
      successRate: Math.round(avgSuccessRate * 10) / 10,
    });
  }, [flows]);

  const statCards: StatCard[] = [
    {
      title: 'Automazioni Totali',
      value: stats.totalFlows,
      icon: Zap,
      color: 'from-emerald-500 to-teal-600',
    },
    {
      title: 'Automazioni Attive',
      value: stats.activeFlows,
      icon: PlayCircle,
      color: 'from-blue-500 to-cyan-600',
    },
    {
      title: 'Messaggi Processati',
      value: stats.messagesProcessed.toLocaleString(),
      change: '+12.5%',
      icon: MessageCircle,
      color: 'from-purple-500 to-pink-600',
    },
    {
      title: 'Tasso di Successo',
      value: `${stats.successRate}%`,
      change: '+0.8%',
      icon: TrendingUp,
      color: 'from-orange-500 to-red-600',
    },
  ];

  // Carica attività recente dai log
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  
  useEffect(() => {
    const logs = dbService.getLogs({ limit: 5 });
    const activity = logs.map((log: any) => {
      const flow = dbService.getFlow(log.flowId);
      const timeAgo = getTimeAgo(new Date(log.timestamp));
      
      return {
        time: timeAgo,
        event: log.message,
        flow: flow?.name || 'Sconosciuto',
        status: log.level === 'error' ? 'error' : log.level === 'info' ? 'success' : 'info'
      };
    });
    setRecentActivity(activity);
  }, []);
  
  // Helper function per calcolare il tempo passato
  const getTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds} sec fa`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min fa`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? 'ora' : 'ore'} fa`;
    const days = Math.floor(hours / 24);
    return `${days} ${days === 1 ? 'giorno' : 'giorni'} fa`;
  };

  const popularTemplates = [
    { name: 'Benvenuto Cliente', icon: '👋', description: 'Saluta automaticamente i nuovi contatti' },
    { name: 'FAQ Automatiche', icon: '❓', description: 'Rispondi alle domande frequenti' },
    { name: 'Promemoria Appuntamenti', icon: '📅', description: 'Invia reminder per appuntamenti' },
    { name: 'Sondaggio Feedback', icon: '📊', description: 'Raccogli opinioni dai clienti' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-caffe-primary dark:text-caffe-accent">
                Dashboard Caffè Sempione
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestisci le comunicazioni WhatsApp per le tue caffetterie di Pogliano e Pero
              </p>
            </div>
            <Link
              to="/editor"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>Nuova Automazione</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-surface rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${stat.color} bg-opacity-10`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  {stat.change && (
                    <span className={`text-sm font-medium ${
                      stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change}
                    </span>
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {stat.title}
                </p>
              </div>
              <div className={`h-1 bg-gradient-to-r ${stat.color}`} />
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Attività Recente
              </h2>
              <Activity className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${
                      activity.status === 'success' ? 'bg-green-500' :
                      activity.status === 'error' ? 'bg-red-500' : 'bg-blue-500'
                    }`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {activity.event}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {activity.flow}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-400">
                    {activity.time}
                  </span>
                </motion.div>
              ))}
            </div>
            <Link
              to="/editor"
              className="mt-4 flex items-center justify-center gap-2 text-sm text-whatsapp-green hover:text-whatsapp-dark transition-colors"
            >
              <span>Vedi tutte le attività</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Azioni Rapide
            </h2>
            <div className="space-y-3">
              <Link
                to="/editor"
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Plus className="w-5 h-5 text-whatsapp-green" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Crea Nuova Automazione
                </span>
              </Link>
              <Link
                to="/flows"
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Activity className="w-5 h-5 text-emerald-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Gestisci Automazioni
                </span>
              </Link>
              <button className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Visualizza Report
                </span>
              </button>
              <button className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left">
                <Users className="w-5 h-5 text-purple-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Gestisci Contatti
                </span>
              </button>
              <Link
                to="/settings"
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Settings className="w-5 h-5 text-orange-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Impostazioni
                </span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Templates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Template Popolari
            </h2>
            <Link
              to="/editor"
              className="text-sm text-whatsapp-green hover:text-whatsapp-dark transition-colors"
            >
              Vedi tutti →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {popularTemplates.map((template, index) => (
              <motion.div
                key={template.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="text-3xl mb-3">{template.icon}</div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {template.description}
                </p>
                <div className="mt-3 flex items-center gap-1 text-whatsapp-green opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-sm">Usa template</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Chart Section */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Messaggi Inviati
              </h2>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">Grafico disponibile a breve</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Distribuzione Automazioni
              </h2>
              <PieChart className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <PieChart className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">Grafico disponibile a breve</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
